import { useEffectivePermissions } from "@/features/authorization/hooks/useEffectivePermissions";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  FileSpreadsheet,
  RefreshCw,
  RotateCcw,
  Upload,
  Users,
  XCircle,
} from "lucide-react";
import {
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";
import { Link } from "react-router-dom";

import {
  importMembers,
  previewMappedMemberImport,
  previewMemberImport,
  type ImportPreviewRow,
  type MemberImportMappings,
  type MemberImportPreview,
  type MemberImportResult,
} from "@/api/migration";
import { Card } from "@/components/ui/card";
import { PageContainer } from "@/design-system";
import { useAuth } from "@/features/auth/hooks/useAuth";

export function DataImportPage() {
  const { churchId } = useAuth();
  const { permissions } = useEffectivePermissions();
  const canHouseholds = permissions.has("HOUSEHOLDS_MANAGE");

  const fileInputRef =
    useRef<HTMLInputElement | null>(null);

  const [selectedFile, setSelectedFile] =
    useState<File | null>(null);

  const [preview, setPreview] =
    useState<MemberImportPreview | null>(null);

  const [mappings, setMappings] =
    useState<MemberImportMappings>({});

  const [isUploading, setIsUploading] =
    useState(false);

  const [isRevalidating, setIsRevalidating] =
    useState(false);

  const [isImporting, setIsImporting] =
    useState(false);

  const [importWarnings, setImportWarnings] =
    useState(false);

  const [
    createHouseholds,
    setCreateHouseholds,
  ] = useState(true);

  const [importResult, setImportResult] =
    useState<MemberImportResult | null>(null);

  const [error, setError] =
    useState<string | null>(null);

  async function handleFile(file: File) {
    if (!churchId) {
      return;
    }

    setSelectedFile(file);
    setPreview(null);
    setMappings({});
    setImportResult(null);
    setImportWarnings(false);
    setCreateHouseholds(canHouseholds);
    setError(null);
    setIsUploading(true);

    try {
      const result = await previewMemberImport(
        churchId,
        file
      );

      setPreview(result);

      setMappings(
        Object.fromEntries(
          result.mappings.map((mapping) => [
            mapping.source_column,
            mapping.suggested_field,
          ])
        )
      );
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "Unable to preview import"
      );
    } finally {
      setIsUploading(false);
    }
  }

  async function revalidateMappings() {
    if (
      !churchId ||
      !selectedFile
    ) {
      return;
    }

    setError(null);
    setIsRevalidating(true);

    try {
      const result =
        await previewMappedMemberImport(
          churchId,
          selectedFile,
          mappings
        );

      setPreview(result);
    } catch (mappingError) {
      setError(
        mappingError instanceof Error
          ? mappingError.message
          : "Unable to revalidate mappings"
      );
    } finally {
      setIsRevalidating(false);
    }
  }

  async function startImport() {
    if (
      !churchId ||
      !selectedFile ||
      !preview
    ) {
      return;
    }

    setError(null);
    setIsImporting(true);

    try {
      const result = await importMembers(
        churchId,
        selectedFile,
        mappings,
        {
          import_warning_rows: importWarnings,
          create_households: createHouseholds && canHouseholds,
        }
      );

      setImportResult(result);
    } catch (importError) {
      setError(
        importError instanceof Error
          ? importError.message
          : "Unable to import members"
      );
    } finally {
      setIsImporting(false);
    }
  }

  function handleFileInputChange(
    event: ChangeEvent<HTMLInputElement>
  ) {
    const file = event.target.files?.[0];

    if (file) {
      void handleFile(file);
    }
  }

  function handleDrop(
    event: DragEvent<HTMLDivElement>
  ) {
    event.preventDefault();

    const file = event.dataTransfer.files?.[0];

    if (file) {
      void handleFile(file);
    }
  }

  function updateMapping(
    sourceColumn: string,
    targetField: string
  ) {
    setMappings((current) => ({
      ...current,
      [sourceColumn]:
        targetField || null,
    }));
  }

  function resetImport() {
    setSelectedFile(null);
    setPreview(null);
    setMappings({});
    setError(null);
    setImportResult(null);
    setImportWarnings(false);
    setCreateHouseholds(canHouseholds);
    setIsUploading(false);
    setIsRevalidating(false);
    setIsImporting(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  if (importResult) {
    return (
      <PageContainer>
        <ImportHero />

        <ImportSuccess
          result={importResult}
          onReset={resetImport}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ImportHero />

      {!preview ? (
        <div className="mt-7">
          <UploadCard
            fileInputRef={fileInputRef}
            selectedFile={selectedFile}
            isUploading={isUploading}
            error={error}
            onFileChange={handleFileInputChange}
            onDrop={handleDrop}
          />
        </div>
      ) : (
        <ImportPreview
          preview={preview}
          mappings={mappings}
          isRevalidating={isRevalidating}
          isImporting={isImporting}
          importWarnings={importWarnings}
          createHouseholds={createHouseholds && canHouseholds}
          error={error}
          onMappingChange={updateMapping}
          onRevalidate={() =>
            void revalidateMappings()
          }
          onImportWarningsChange={
            setImportWarnings
          }
          onCreateHouseholdsChange={
            setCreateHouseholds
          }
          onImport={() => void startImport()}
          onReset={resetImport}
        />
      )}
    </PageContainer>
  );
}

function ImportHero() {
  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-indigo-950/30 px-7 py-8 shadow-xl shadow-black/20">
      <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />

      <div className="relative">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-400">
          Settings
        </p>

        <h1 className="mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
          Data Import
        </h1>

        <p className="mt-3 max-w-2xl text-base leading-7 text-slate-300">
          Bring your existing member records into MinistryIQ.
          Map your CSV columns, validate the data, and import
          members and households safely.
        </p>
      </div>
    </section>
  );
}

function UploadCard({
  fileInputRef,
  selectedFile,
  isUploading,
  error,
  onFileChange,
  onDrop,
}: {
  fileInputRef:
    React.RefObject<HTMLInputElement | null>;
  selectedFile: File | null;
  isUploading: boolean;
  error: string | null;
  onFileChange: (
    event: ChangeEvent<HTMLInputElement>
  ) => void;
  onDrop: (
    event: DragEvent<HTMLDivElement>
  ) => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-5">
        <h2 className="text-xl font-semibold text-white">
          Import Members
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Upload a CSV export from your current church system
          or spreadsheet.
        </p>
      </div>

      <div className="p-6">
        <div
          onDragOver={(event) =>
            event.preventDefault()
          }
          onDrop={onDrop}
          className="flex min-h-[360px] flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-700 bg-slate-950/40 px-8 text-center transition hover:border-indigo-500/50 hover:bg-indigo-500/5"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-indigo-500/15 text-indigo-400">
            <Upload size={30} />
          </div>

          <h3 className="mt-6 text-xl font-semibold text-white">
            Drop your CSV file here
          </h3>

          <p className="mt-2 text-sm text-slate-400">
            or choose a file from your computer
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFileChange}
            className="hidden"
          />

          <button
            type="button"
            disabled={isUploading}
            onClick={() =>
              fileInputRef.current?.click()
            }
            className="mt-6 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isUploading
              ? "Reviewing File..."
              : "Choose CSV File"}
          </button>

          {selectedFile && (
            <div className="mt-6 flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 px-4 py-3">
              <FileSpreadsheet
                size={20}
                className="text-emerald-400"
              />

              <div className="text-left">
                <p className="text-sm font-medium text-white">
                  {selectedFile.name}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  {formatFileSize(
                    selectedFile.size
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <ErrorMessage message={error} />
        )}

        <div className="mt-6 rounded-xl border border-slate-800 bg-slate-950/40 px-5 py-4">
          <p className="text-sm font-semibold text-white">
            Before you upload
          </p>

          <p className="mt-2 text-sm leading-6 text-slate-400">
            Your CSV must include a header row. First name and
            last name are required. Duplicate and invalid rows
            will not be imported.
          </p>
        </div>
      </div>
    </Card>
  );
}

function ImportPreview({
  preview,
  mappings,
  isRevalidating,
  isImporting,
  importWarnings,
  createHouseholds,
  error,
  onMappingChange,
  onRevalidate,
  onImportWarningsChange,
  onCreateHouseholdsChange,
  onImport,
  onReset,
}: {
  preview: MemberImportPreview;
  mappings: MemberImportMappings;
  isRevalidating: boolean;
  isImporting: boolean;
  importWarnings: boolean;
  createHouseholds: boolean;
  error: string | null;
  onMappingChange: (
    sourceColumn: string,
    targetField: string
  ) => void;
  onRevalidate: () => void;
  onImportWarningsChange: (
    value: boolean
  ) => void;
  onCreateHouseholdsChange: (
    value: boolean
  ) => void;
  onImport: () => void;
  onReset: () => void;
}) {
  const issueRows = preview.rows.filter(
    (row) => row.status !== "READY"
  );

  return (
    <>
      <div className="mt-7 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm text-slate-500">
            Reviewing
          </p>

          <div className="mt-1 flex items-center gap-3">
            <FileSpreadsheet
              size={22}
              className="text-emerald-400"
            />

            <h2 className="text-xl font-semibold text-white">
              {preview.file_name}
            </h2>
          </div>
        </div>

        <button
          type="button"
          disabled={isImporting}
          onClick={onReset}
          className="flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-semibold text-slate-300 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <RotateCcw size={16} />
          Choose Another File
        </button>
      </div>

      {error && (
        <ErrorMessage message={error} />
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatusCard
          label="Total Rows"
          value={preview.total_rows}
          icon={<FileSpreadsheet size={23} />}
          className="bg-indigo-500/15 text-indigo-400"
        />

        <StatusCard
          label="Ready"
          value={preview.ready_rows}
          icon={<CheckCircle2 size={23} />}
          className="bg-emerald-500/15 text-emerald-400"
        />

        <StatusCard
          label="Warnings"
          value={preview.warning_rows}
          icon={<AlertTriangle size={23} />}
          className="bg-amber-500/15 text-amber-400"
        />

        <StatusCard
          label="Duplicates"
          value={preview.duplicate_rows}
          icon={<Users size={23} />}
          className="bg-violet-500/15 text-violet-400"
        />

        <StatusCard
          label="Invalid"
          value={preview.invalid_rows}
          icon={<XCircle size={23} />}
          className="bg-rose-500/15 text-rose-400"
        />
      </div>

      <div className="mt-7">
        <EditableFieldMappingCard
          preview={preview}
          mappings={mappings}
          isRevalidating={isRevalidating}
          isImporting={isImporting}
          onMappingChange={onMappingChange}
          onRevalidate={onRevalidate}
        />
      </div>

      <div className="mt-7 grid gap-6 xl:grid-cols-[0.85fr_1.15fr]">
        <ValidationCard rows={issueRows} />

        <ImportRowsTable rows={preview.rows} />
      </div>

      <ImportActionCard
        preview={preview}
        isImporting={isImporting}
        importWarnings={importWarnings}
        createHouseholds={createHouseholds}
        onImportWarningsChange={
          onImportWarningsChange
        }
        onCreateHouseholdsChange={
          onCreateHouseholdsChange
        }
        onImport={onImport}
      />
    </>
  );
}

function EditableFieldMappingCard({
  preview,
  mappings,
  isRevalidating,
  isImporting,
  onMappingChange,
  onRevalidate,
}: {
  preview: MemberImportPreview;
  mappings: MemberImportMappings;
  isRevalidating: boolean;
  isImporting: boolean;
  onMappingChange: (
    sourceColumn: string,
    targetField: string
  ) => void;
  onRevalidate: () => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-4 border-b border-slate-800 px-6 py-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Field Mapping
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Match each CSV column to a MinistryIQ member field.
          </p>
        </div>

        <button
          type="button"
          disabled={
            isRevalidating ||
            isImporting
          }
          onClick={onRevalidate}
          className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <RefreshCw
            size={16}
            className={
              isRevalidating
                ? "animate-spin"
                : ""
            }
          />

          {isRevalidating
            ? "Revalidating..."
            : "Apply Mappings"}
        </button>
      </div>

      <div className="grid gap-px bg-slate-800 md:grid-cols-2">
        {preview.mappings.map((mapping) => (
          <div
            key={mapping.source_column}
            className="bg-slate-900 px-6 py-5"
          >
            <label className="text-sm font-semibold text-white">
              {mapping.source_column}
            </label>

            <p className="mt-1 text-xs text-slate-600">
              CSV column
            </p>

            <div className="mt-4 flex items-center gap-3">
              <ArrowRight
                size={16}
                className="shrink-0 text-slate-600"
              />

              <select
                disabled={isImporting}
                value={
                  mappings[
                    mapping.source_column
                  ] ?? ""
                }
                onChange={(event) =>
                  onMappingChange(
                    mapping.source_column,
                    event.target.value
                  )
                }
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-sm text-white outline-none focus:border-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">
                  Do Not Import
                </option>

                {preview.fields.map((field) => (
                  <option
                    key={field.field}
                    value={field.field}
                  >
                    {field.label}
                    {field.required
                      ? " *"
                      : ""}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ImportActionCard({
  preview,
  isImporting,
  importWarnings,
  createHouseholds,
  onImportWarningsChange,
  onCreateHouseholdsChange,
  onImport,
}: {
  preview: MemberImportPreview;
  isImporting: boolean;
  importWarnings: boolean;
  createHouseholds: boolean;
  onImportWarningsChange: (
    value: boolean
  ) => void;
  onCreateHouseholdsChange: (
    value: boolean
  ) => void;
  onImport: () => void;
}) {
  const { permissions } = useEffectivePermissions();
  const importableRows =
    preview.ready_rows +
    (
      importWarnings
        ? preview.warning_rows
        : 0
    );

  return (
    <Card className="mt-7 overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-5">
        <h2 className="text-xl font-semibold text-white">
          Ready to Import
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Duplicate and invalid rows will always be skipped.
        </p>
      </div>

      <div className="p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <ImportOption
            label="Import warning rows"
            description={`${preview.warning_rows} warning rows available`}
            checked={importWarnings}
            disabled={isImporting}
            onChange={onImportWarningsChange}
          />

          <ImportOption
            label="Create households"
            description={permissions.has("HOUSEHOLDS_MANAGE") ? "Create and link missing households" : "Household management permission required"}
            checked={createHouseholds}
            disabled={isImporting || !permissions.has("HOUSEHOLDS_MANAGE")}
            onChange={onCreateHouseholdsChange}
          />

          <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
              Members to Import
            </p>

            <p className="mt-2 text-3xl font-bold text-white">
              {importableRows}
            </p>

            <p className="mt-2 text-xs text-slate-600">
              {preview.duplicate_rows} duplicates and{" "}
              {preview.invalid_rows} invalid rows skipped
            </p>
          </div>
        </div>

        <button
          type="button"
          disabled={
            isImporting ||
            importableRows === 0
          }
          onClick={onImport}
          className="mt-6 w-full rounded-xl bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isImporting
            ? "Importing Members..."
            : `Import ${importableRows} Members`}
        </button>
      </div>
    </Card>
  );
}

function ImportOption({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) =>
          onChange(event.target.checked)
        }
        className="mt-1 h-4 w-4 rounded border-slate-700 disabled:cursor-not-allowed"
      />

      <span>
        <span className="block font-semibold text-white">
          {label}
        </span>

        <span className="mt-1 block text-xs text-slate-500">
          {description}
        </span>
      </span>
    </label>
  );
}

function ImportSuccess({
  result,
  onReset,
}: {
  result: MemberImportResult;
  onReset: () => void;
}) {
  return (
    <Card className="mt-7 overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-8 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-400">
          <CheckCircle2 size={34} />
        </div>

        <h2 className="mt-5 text-2xl font-bold text-white">
          Import Complete
        </h2>

        <p className="mt-2 text-sm text-slate-400">
          {result.file_name} was processed successfully.
        </p>
      </div>

      <div className="grid gap-px bg-slate-800 sm:grid-cols-2 xl:grid-cols-5">
        <ImportResultMetric
          label="Members Imported"
          value={result.members_imported}
        />

        <ImportResultMetric
          label="Households Created"
          value={result.households_created}
        />

        <ImportResultMetric
          label="Duplicates Skipped"
          value={result.duplicates_skipped}
        />

        <ImportResultMetric
          label="Invalid Skipped"
          value={result.invalid_rows_skipped}
        />

        <ImportResultMetric
          label="Warnings Skipped"
          value={result.warning_rows_skipped}
        />
      </div>

      <div className="flex flex-col gap-4 px-6 py-6 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-slate-500">
          Completed in {result.duration_ms} ms
        </p>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={onReset}
            className="rounded-xl border border-slate-700 px-5 py-3 text-sm font-semibold text-slate-300 transition hover:text-white"
          >
            Import Another File
          </button>

          <Link
            to="/members"
            className="rounded-xl bg-indigo-600 px-5 py-3 text-center text-sm font-semibold text-white transition hover:bg-indigo-500"
          >
            View Members
          </Link>
        </div>
      </div>
    </Card>
  );
}

function ImportResultMetric({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <div className="bg-slate-900 px-6 py-6 text-center">
      <p className="text-3xl font-bold text-white">
        {value}
      </p>

      <p className="mt-2 text-xs font-medium text-slate-500">
        {label}
      </p>
    </div>
  );
}

function StatusCard({
  label,
  value,
  icon,
  className,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  className: string;
}) {
  return (
    <Card className="p-5">
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-xl ${className}`}
      >
        {icon}
      </div>

      <p className="mt-5 text-sm font-medium text-slate-400">
        {label}
      </p>

      <p className="mt-2 text-4xl font-bold text-white">
        {value}
      </p>
    </Card>
  );
}

function ValidationCard({
  rows,
}: {
  rows: ImportPreviewRow[];
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-5">
        <h2 className="text-xl font-semibold text-white">
          Validation Issues
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Invalid, warning, and duplicate rows requiring review.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="flex min-h-[260px] flex-col items-center justify-center p-8 text-center">
          <CheckCircle2
            size={34}
            className="text-emerald-400"
          />

          <p className="mt-4 font-semibold text-white">
            No validation issues
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-800">
          {rows.map((row) => (
            <div
              key={row.row_number}
              className="px-6 py-5"
            >
              <div className="flex items-center justify-between gap-4">
                <p className="font-semibold text-white">
                  Row {row.row_number}
                </p>

                <RowStatus status={row.status} />
              </div>

              {row.issues.length > 0 && (
                <div className="mt-3 space-y-2">
                  {row.issues.map((issue, index) => (
                    <div
                      key={`${row.row_number}-${issue.field}-${index}`}
                      className="flex items-start gap-2 text-sm text-rose-300"
                    >
                      <XCircle
                        size={15}
                        className="mt-0.5 shrink-0"
                      />

                      <span>
                        {issue.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {row.duplicate && (
                <div className="mt-4 rounded-xl border border-violet-500/20 bg-violet-500/5 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-violet-400">
                    {row.duplicate.match_type} duplicate
                  </p>

                  <p className="mt-2 font-semibold text-white">
                    {row.duplicate.existing_member_name}
                  </p>

                  <p className="mt-1 text-sm text-slate-400">
                    {row.duplicate.existing_email || "No email"}
                  </p>

                  <p className="mt-1 text-sm text-slate-500">
                    {row.duplicate.existing_phone || "No phone"}
                  </p>

                  <p className="mt-3 text-xs text-violet-300">
                    Match confidence:{" "}
                    {row.duplicate.confidence}%
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ImportRowsTable({
  rows,
}: {
  rows: ImportPreviewRow[];
}) {
  return (
    <Card className="overflow-hidden">
      <div className="border-b border-slate-800 px-6 py-5">
        <h2 className="text-xl font-semibold text-white">
          Import Preview
        </h2>

        <p className="mt-1 text-sm text-slate-500">
          Review the mapped member and household data.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-950/60 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-6 py-4">
                Row
              </th>

              <th className="px-6 py-4">
                Member
              </th>

              <th className="px-6 py-4">
                Email
              </th>

              <th className="px-6 py-4">
                Household
              </th>

              <th className="px-6 py-4">
                Status
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-800">
            {rows.map((row) => {
              const memberName = [
                getStringValue(
                  row.values.first_name
                ),
                getStringValue(
                  row.values.last_name
                ),
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <tr
                  key={row.row_number}
                  className="transition hover:bg-slate-800/30"
                >
                  <td className="px-6 py-4 text-slate-500">
                    {row.row_number}
                  </td>

                  <td className="px-6 py-4 font-medium text-white">
                    {memberName || "Missing name"}
                  </td>

                  <td className="px-6 py-4 text-slate-400">
                    {getStringValue(
                      row.values.email
                    ) || "-"}
                  </td>

                  <td className="px-6 py-4 text-slate-400">
                    {getStringValue(
                      row.values.household_name
                    ) || "-"}
                  </td>

                  <td className="px-6 py-4">
                    <RowStatus status={row.status} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RowStatus({
  status,
}: {
  status: ImportPreviewRow["status"];
}) {
  const className =
    status === "READY"
      ? "bg-emerald-500/10 text-emerald-300"
      : status === "WARNING"
        ? "bg-amber-500/10 text-amber-300"
        : status === "DUPLICATE"
          ? "bg-violet-500/10 text-violet-300"
          : "bg-rose-500/10 text-rose-300";

  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${className}`}
    >
      {status}
    </span>
  );
}

function ErrorMessage({
  message,
}: {
  message: string;
}) {
  return (
    <div className="mt-5 rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-sm text-rose-300">
      {message}
    </div>
  );
}

function getStringValue(
  value: unknown
): string {
  return typeof value === "string"
    ? value
    : "";
}

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} bytes`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(
    size /
    (1024 * 1024)
  ).toFixed(1)} MB`;
}

